<template>
    <v-container fluid>
        <page-header title="Matches" icon="mdi-gamepad-variant-outline">
            <template v-slot:actions>
                <v-btn v-if="csvEnabled" class="white--text" depressed small color="black" @click="downloadMatchesCSV">
                    <v-icon left small>mdi-download</v-icon>Export CSV
                </v-btn>
                <v-btn v-if="csvEnabled" class="white--text" depressed small color="black" @click="showImportDialog = true">
                    <v-icon left small>mdi-upload</v-icon>Upload CSV
                </v-btn>
                <v-btn depressed small outlined @click="fixPlayTimeDialog = true">
                    <v-icon left small>mdi-timer-refresh-outline</v-icon>Recalculate minutes
                </v-btn>
                <v-btn class="white--text" depressed small color="black" @click="createMatch">
                    <v-icon left small>mdi-plus</v-icon>Create
                </v-btn>
            </template>
            <table-filters :fields="filterFields" :value="filterValues" @apply="applyFilters" />
        </page-header>
        <import-dialog
            :open-dialog="showImportDialog"
            entity-label="Matches CSV"
            :successfull-import="successfullImport"
            :message="message"
            file-type=".csv,text/csv"
            @file-import="uploadMatches"
            @close-import-dialog="closeImportDialog"
        >
            <csv-help :columns="csvColumns" :notes="csvNotes" />
        </import-dialog>
        <array-view
            :items="matches"
            :headers="displayInfo"
            :total-pages="totalPages"
            :page="page"
            :size="size"
            :sort-by="sort.sortBy"
            :sort-desc="sort.sortDesc"
            :type="'match'"
            @onDelete="onDelete"
            @handleClick="handleClick"
            @paginationChange="paginationChange"
            @sortChange="changeSort"
        />
        <!-- the minutes of every player of every match, again from the substitutions in the play-by-play -->
        <confirm-dialog
            v-model="fixPlayTimeDialog"
            title="Recalculate the minutes?"
            text="The playing time of every player in every match is calculated again from the substitutions of the play-by-play. It can take a minute."
            confirm-text="Recalculate"
            color="black"
            icon="mdi-timer-refresh-outline"
            :loading="fixingPlayTime"
            @confirm="fixPlayTime"
        />
        <v-snackbar v-model="fixPlayTimeMessage.show" :color="fixPlayTimeMessage.color" timeout="6000">
            {{ fixPlayTimeMessage.text }}
        </v-snackbar>
    </v-container>
</template>

<script>
    import matchService from '@/services/match'
    import ArrayView from '@/components/ArrayView'
    import teamService from '@/services/team'
    import seasonService from '@/services/season'
    import leagueService from '@/services/league'
    import cupService from '@/services/cup'
    import TableFilters from '@/components/TableFilters'
    import PageHeader from '@/components/PageHeader'
    import listTable from '@/mixins/listTable'
    import ImportDialog from '@/components/ImportDialog'
    import CsvHelp from '@/components/CsvHelp'
    import ConfirmDialog from '@/components/ConfirmDialog'

    export default {
        name: 'Match',
        components: { ArrayView, TableFilters, ImportDialog, PageHeader, CsvHelp, ConfirmDialog },
        mixins: [listTable],
        data: () => {
            return {
                successfullImport: null,
                showImportDialog: false,
                fixPlayTimeDialog: false,
                fixingPlayTime: false,
                fixPlayTimeMessage: { show: false, text: '', color: 'success' },
                // the columns of the matches CSV, shown in the import dialog
                csvColumns: [
                    { name: 'id', text: 'Empty for a new match; the id of an existing match to update it.' },
                    { name: 'team1Id, team2Id', text: 'Required, the ids of the two teams.' },
                    { name: 'seasonId', text: 'Required, the id of the season.' },
                    { name: 'arenaId', text: 'Required, the id of the court.' },
                    { name: 'matchDatetime', text: 'Optional, in UTC as 2025-11-25T19:00:00Z (21:00 Greek winter time).' },
                    { name: 'leagueId, cupLeagueId', text: 'Optional, the id of the league / of the cup league.' },
                    { name: 'phase', text: 'Optional: regular_season, playoffs or placements (regular_season when empty).' },
                    { name: 'round', text: 'Optional number (0 when empty).' },
                    { name: 'customRoundText', text: 'Optional, a text in place of the round (e.g. Τελικός).' },
                    { name: 'scoreTeam1, scoreTeam2', text: 'Optional numbers (0 when empty).' },
                    { name: 'videoUrl', text: 'Optional, the link of the video.' },
                    { name: 'isCup, postponed', text: 'Optional yes/no: true or false (false when empty).' },
                    { name: 'publishedOnSocial', text: 'Optional yes/no: true or false; empty keeps the current value (false for a new match).' },
                    { name: 'mvp…', text: 'Only in the export, they are not read on import.' }
                ],
                csvNotes: [
                    'A row with an id <b>replaces</b> the fields above of that match: an empty optional cell clears it (matchDatetime and publishedOnSocial are kept).',
                    'If a row has an error, nothing of the file is imported.'
                ],
                csvEnabled: true,
                matches: [],
                displayInfo: [
                    {
                        text: 'Id',
                        value: 'id'
                    },
                    {
                        text: 'Team 1',
                        value: 'team1',
                        subLevel: 'name'
                    },
                    {
                        text: 'Team 2',
                        value: 'team2',
                        subLevel: 'name'
                    },
                    {
                        text: 'Score Team 1',
                        value: 'scoreTeam1'
                    },
                    {
                        text: 'Score Team 2',
                        value: 'scoreTeam2'
                    },
                    {
                        text: 'Season',
                        value: 'season',
                        subLevel: 'name'
                    },
                    {
                        text: 'Date',
                        value: 'matchDatetime'
                    },
                    {
                        text: 'Phase',
                        value: 'phase'
                    },
                    {
                        text: 'Type',
                        value: 'season',
                        subLevel: 'type'
                    },
                    {
                        text: 'Cup',
                        value: 'isCup'
                    },
                    {
                        text: 'League',
                        value: 'league',
                        sortable: false
                    },
                    {
                        text: 'Cup League',
                        value: 'cupLeague',
                        subLevel: 'title'
                    },
                    {
                        text: 'Round',
                        value: 'round'
                    },
                    {
                        // kept as data, but out of the site, the rankings and the stats
                        text: 'Excluded',
                        value: 'excluded'
                    },
                    {
                        // posted on the social media of the league: switch + the score/MVP to post
                        text: 'Social',
                        value: 'publishedOnSocial'
                    }
                ],
                tableKey: 'matches',
                filterFields: [
                    { key: 'round', label: 'Round', type: 'number' },
                    { key: 'season', label: 'Season', type: 'autocomplete', loadItems: () => seasonService.getSeasons().then(resp => resp.data.content) },
                    { key: 'league', label: 'League', type: 'autocomplete', itemText: 'title', loadItems: () => leagueService.getAllLeagues().then(resp => resp.data) },
                    { key: 'phase', label: 'Phase', type: 'select', items: ['placements', 'regular_season', 'playoffs'] },
                    { key: 'team1', label: 'Home Team', type: 'autocomplete', loadItems: () => teamService.getTeams().then(resp => resp.data.content) },
                    { key: 'team2', label: 'Away Team', type: 'autocomplete', loadItems: () => teamService.getTeams().then(resp => resp.data.content) },
                    { key: 'cupLeague', label: 'Cup League', type: 'autocomplete', itemText: 'title', loadItems: () => cupService.getAllCups().then(resp => resp.data) },
                    { key: 'isCup', label: 'Cup', type: 'boolean' },
                    { key: 'publishedOnSocial', label: 'Published On Social', type: 'boolean' },
                    { key: 'postponed', label: 'Postponed', type: 'boolean' },
                    { key: 'excluded', label: 'Excluded', type: 'boolean' },
                    { key: 'matchDatetime', label: 'Date', type: 'dateRange' }
                ],
                size: 100,
                totalPages: 0,
                page: 1,
                loading: false,
                message: null
            }
        },
        created() {
            this.fetchItems()
        },
        methods: {
            downloadMatchesCSV() {
                matchService.downloadMatchesCSV(this.filterQuery).then((response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]))
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', 'matches.csv')
                    document.body.appendChild(link)
                    link.click()
                })
            },
            closeImportDialog() {
                this.showImportDialog = false
                // reset state so dialog shows proper buttons next time
                this.successfullImport = null
            },
            uploadMatches(file) {
                this.successfullImport = null
                const formData = new FormData()
                formData.append('file', file)
                matchService.uploadMatchesCSV(formData).then(() => {
                    this.successfullImport = true
                    this.fetchItems()
                    this.closeImportDialog()
                }).catch((error) => {
                    this.successfullImport = false
                    this.message = error.response.data.message || 'Import failed'
                })
            },
            handleClick(action, item) {
                if (action === 'togglePublishedOnSocial') {
                    matchService.togglePublishedOnSocial(item.id, item.publishedOnSocial).catch(() => {
                        item.publishedOnSocial = !item.publishedOnSocial
                    })
                    return
                }
                this.$router.push(`/matches/${item.id}/${action}`)
            },
            onDelete(item, page) {
                matchService.deleteMatch(item.id).then(() => {
                    this.page = page
                    this.fetchItems()
                })
            },
            fixPlayTime() {
                this.fixingPlayTime = true
                matchService.fixPlayTime().then(resp => {
                    const count = resp.data && resp.data.content
                    this.fixPlayTimeMessage = { show: true, text: `The minutes of ${count} player stats were calculated again.`, color: 'success' }
                }).catch(() => {
                    this.fixPlayTimeMessage = { show: true, text: 'The minutes could not be calculated again.', color: 'error' }
                }).finally(() => {
                    this.fixingPlayTime = false
                    this.fixPlayTimeDialog = false
                })
            },
            createMatch() {
                this.$router.push(`/matches/create`)
            },
            fetchItems() {
                this.loading = true
                matchService.getFilteredMatches(this.filterQuery, this.page, this.size, this.sort).then(resp => {
                    this.matches = resp.data.content
                    this.totalPages = resp.data.totalPages
                    this.loading = false
                })
            }
        }
    }
</script>

<style scoped>
.card-title {
    display: flex;
}

.v-list-item {
    min-height: 30px !important;
}

.v-card {
    min-width: 200px;
    position: relative;
}

.header-card {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1%;
}

.header-buttons {
    align-self: center;
}

.edit-button:hover {
    background-color: whitesmoke;
    color: white;
}

.promo-category-card {
    padding: 4%;
}

.overflow-text {
    max-width: 79%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.pagination {
    justify-content: center;
}
</style>
